import { useMemo, MutableRefObject } from 'react';
import {
	DlobWebsocketClient,
	DlobWebsocketClientConfig,
	ResultSlotIncrementer,
} from '@drift/common';
import useOrderbookDisplayStore, {
	OrderbookSource,
} from 'src/stores/orderbook/useOrderbookDisplayStore';
import useFeatureFlagStore from 'src/stores/featureFlag/useFeatureFlagStore';
import { WEBSOCKET_URL } from '../constants';

interface UseWebsocketClientParams {
	clientRef: MutableRefObject<DlobWebsocketClient | null>;
	resultSlotIncrementer: MutableRefObject<ResultSlotIncrementer>;
	lastFallbackTimestamp: MutableRefObject<number>;
	subscriptionRestartKey: number;
}

/**
 * Hook that creates and manages the DlobWebsocketClient.
 * The client is created lazily and stored in the provided ref.
 */
export function useWebsocketClient({
	clientRef,
	resultSlotIncrementer,
	lastFallbackTimestamp,
	subscriptionRestartKey,
}: UseWebsocketClientParams): DlobWebsocketClient {
	const enableIndicativeOrderbook = useFeatureFlagStore((s) =>
		s.flagEnabled('ENABLE_INDICATIVE_ORDERBOOK')
	);
	const setOrderbookStore = useOrderbookDisplayStore((s) => s.set);

	const client = useMemo(() => {
		if (clientRef.current) {
			return clientRef.current;
		}

		resultSlotIncrementer.current.resetAll();

		const config: DlobWebsocketClientConfig = {
			websocketUrl: WEBSOCKET_URL,
			enableIndicativeOrderbook,
			resultSlotIncrementer: resultSlotIncrementer.current,
			onFallback: () => {
				lastFallbackTimestamp.current = Date.now();
				setOrderbookStore((s) => {
					if (!s.disableAutoSourceSwitching) {
						s.orderbookSource = OrderbookSource.POLLING;
					}
					s.waitingForNewGroupingData = false;
				});
			},
		};

		const newClient = new DlobWebsocketClient(config);
		clientRef.current = newClient;
		return newClient;
	}, [
		clientRef,
		resultSlotIncrementer,
		lastFallbackTimestamp,
		enableIndicativeOrderbook,
		setOrderbookStore,
		subscriptionRestartKey,
	]);

	return client;
}
